import {useState, useRef, useEffect, useMemo, useCallback} from "react";
import {
    Box,
    BoxProps,
    Input,
    Tag,
    TagLabel,
    TagCloseButton,
    Popover,
    PopoverArrow,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Button,
    Flex,
    useOutsideClick,
    List,
    ListItem,
    Avatar,
    Text,
    IconButton,
    Tooltip,
    Spinner,
} from "@chakra-ui/react";
import {AddIcon} from "@chakra-ui/icons";
import {InputProps, SuggestionData, TagUser} from "../../models";
import {isValidEmail} from "../../helper/utils";

interface UserTagProps {
    defineIcon?: (s: SuggestionData) => string | undefined
    fetchUsers: (query: string) => Promise<SuggestionData[]>
    maxVisibleTags?: number
    onAddition: (val: TagUser) => void
    onRemoval: (val: string) => void
    disabled?: boolean
}

const schemes: Record<TagUser["type"], string> = {
    "non-existent": "grey",
    unverified: "blue",
    verified: "primary"
};

function debounce(func: (...args: any[]) => void, delay: number) {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
}

function UserTag(t: {label: string, scheme: string, onClick: VoidFunction} & BoxProps) {
    return <Tag
        size="md"
        borderRadius="full"
        variant="outline"
        colorScheme={t.scheme}
        flex={t.flex}
        justifyContent="space-between"
    >
        <TagLabel>{t.label}</TagLabel>
        <TagCloseButton onClick={t.onClick} />
    </Tag>;
}

export function UserTagInput(p: InputProps<TagUser[]> & UserTagProps) {
    const [query, setQuery] = useState("");
    const [filteredData, setFilteredData] = useState<SuggestionData[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const selection = useMemo(function () {
        if (!p.currentValue) {
            return [];
        }
        return p.currentValue;
    }, [p.currentValue]);
    const fetchUserSuggestions = useCallback(debounce(
        async function (query: string) {
            if (query.trim() === "") {
                setFilteredData([]);
                return;
            }
            try {
                setIsLoading(true);
                const users = await p.fetchUsers(query);
                setFilteredData(users);
                setShowSuggestions(users.length > 0);
            } catch (err) {
                setFilteredData([]);
            } finally {
                setIsLoading(false);
            }
        },
        300
    ), [p.fetchUsers]);
    const maxTags = p.maxVisibleTags ?? 5;

    useOutsideClick({ref, handler: () => setShowSuggestions(false)});
    useEffect(() => {
        if (query.length > 2) {
            fetchUserSuggestions(query);
        }
    }, [query, fetchUserSuggestions]);

    function handleSelectUser(data: SuggestionData) {
        const {value} = data;
        p.onAddition({
            email: value.email,
            type: value.verified ? "verified" : "unverified"
        });
        setQuery("");
        setShowSuggestions(false);
    }

    function handleAddEmailUser() {
        if (isValidEmail(query)) {
            p.onAddition({email: query, type: "non-existent"});
            setQuery("");
            setShowSuggestions(false);
        }
    }
    function handleKey(code: string) {
        if (code === "Enter" && isValidEmail(query)) {
            handleAddEmailUser();
        }
    }
    return <Box ref={ref} position="relative">
        <Flex flexWrap="wrap" gap={2} mb={2}>
            {selection.slice(0, p.maxVisibleTags ?? 5).map((t) => <UserTag
                key={t.email}
                label={t.email}
                scheme={schemes[t.type]}
                onClick={() => p.onRemoval(t.email)}
            />)}
            {selection.length > maxTags && <Popover placement="top">
                <PopoverTrigger>
                    <Button size="sm" variant="link" colorScheme="gray">
                        +{selection.length - maxTags} more
                    </Button>
                </PopoverTrigger>
                <PopoverContent>
                    <PopoverArrow/>
                    <PopoverBody>
                        <List>
                            {selection.slice(maxTags).map((t) => <ListItem key={t.email} display="flex" alignItems="stretch" py={1}>
                                <UserTag
                                    label={t.email}
                                    scheme={schemes[t.type]}
                                    onClick={() => p.onRemoval(t.email)}
                                    flex="2 1 auto"
                                />
                            </ListItem>)}
                        </List>
                    </PopoverBody>
                </PopoverContent>
            </Popover>}
        </Flex>
        <Flex px="2px" alignItems="center">
            <Input
                aria-describedby={p.description ? p.id + "_desc": undefined}
                id={p.id}
                placeholder={p.placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => query.trim() && setShowSuggestions(true)}
                flex={1}
                borderRadius="8px"
                backgroundColor="#F5F7F9"
                height="58px"
                focusBorderColor={p.error ? "error" : "primary.500"}
                border="1px solid transaparent"
                transition="all .35s ease"
                onKeyUp={(e) => handleKey(e.code)}
                disabled={p.disabled}
            />
            {!p.disabled && isValidEmail(query) && <Tooltip label="Add email" hasArrow>
                <IconButton
                    aria-label="Add email"
                    icon={<AddIcon />}
                    onClick={handleAddEmailUser}
                    ml={2}
                />
            </Tooltip>}
        </Flex>
        {isLoading && <Flex justify="center" mt={2}> <Spinner size="sm" /> </Flex>}
        {showSuggestions && !isLoading && <Box
            position="absolute"
            zIndex={10}
            bg="white"
            w="100%"
            borderRadius="md"
            boxShadow="md"
            mt={1}
            maxH="200px"
            overflowY="auto"
        >
            {filteredData.map(function (data) {
                return <Flex
                    align="center"
                    key={data.subtitle}
                    p={2}
                    cursor="pointer"
                    _hover={{bg: "gray.100"}}
                    onClick={() => handleSelectUser(data)}
                >
                    <Avatar name={data.title} src={p?.defineIcon?.(data)} size="md" mr={2}/>
                    <div className="stack">
                        <Text fontWeight="bold">{data.title}</Text>
                        <Text>{data.subtitle}</Text>
                    </div>
                </Flex>;
            })}
        </Box>
        }
    </Box>;
};
